import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

const ImageGallery = ({ images, productId }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const nextImage = (e) => {
    if (e) e.stopPropagation();
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    if (e) e.stopPropagation();
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isLightboxOpen) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') setIsLightboxOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, images.length]); // Added images.length to dependency array for next/prevImage stability

  return (
    <div className="space-y-6">
      {/* Main Large Image */}
      <div
        className="relative aspect-[3/4] w-full overflow-hidden bg-gray-100 cursor-zoom-in"
        onClick={() => setIsLightboxOpen(true)}
      >
        <AnimatePresence mode='wait'>
          <motion.img
            key={selectedImage}
            src={images[selectedImage].src}
            alt={images[selectedImage].alt}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>

        {/* Hover Overlay Hint */}
        <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
          <span className="text-white opacity-0 hover:opacity-100 transition-opacity duration-300 font-bold tracking-widest uppercase text-sm bg-black/50 px-4 py-2 backdrop-blur-sm rounded-full">
            Ampliar
          </span>
        </div>
      </div>

      {/* Minimalist Thumbnails */}
      <div className="flex space-x-4 overflow-x-auto pb-2 no-scrollbar">
        {images.map((image, index) => (
          <button
            key={index}
            onClick={() => setSelectedImage(index)}
            className={`relative flex-shrink-0 w-20 h-24 overflow-hidden transition-all duration-300 ${selectedImage === index ? 'opacity-100 ring-1 ring-charcoal' : 'opacity-50 hover:opacity-100'
              }`}
          >
            <img
              src={image.src}
              alt={image.alt}
              className="w-full h-full object-cover"
            />
          </button>
        ))}
      </div>

      {/* Lightbox Overlay */}
      {createPortal(
        <AnimatePresence>
          {isLightboxOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
              onClick={() => setIsLightboxOpen(false)}
            >
              {/* Close Button */}
              <button
                className="absolute top-6 right-6 text-white text-4xl font-light hover:opacity-70 z-50"
                onClick={() => setIsLightboxOpen(false)}
              >
                &times;
              </button>

              {/* Navigation Buttons */}
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-6xl font-thin hover:opacity-70 z-50 hidden md:block p-4"
                onClick={prevImage}
              >
                &#8249;
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-6xl font-thin hover:opacity-70 z-50 hidden md:block p-4"
                onClick={nextImage}
              >
                &#8250;
              </button>

              {/* Image Container */}
              <motion.div
                className="relative max-w-5xl max-h-[90vh] w-full h-full flex items-center justify-center"
                onClick={(e) => e.stopPropagation()} // Prevent closing when clicking image
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={(e, { offset, velocity }) => {
                  const swipe = offset.x;
                  if (swipe < -50) nextImage();
                  else if (swipe > 50) prevImage();
                }}
              >
                <motion.img
                  key={selectedImage}
                  src={images[selectedImage].src}
                  alt={images[selectedImage].alt}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="max-w-full max-h-full object-contain shadow-2xl"
                />
              </motion.div>

              {/* Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm tracking-widest">
                {selectedImage + 1} / {images.length}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default ImageGallery;